import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";

import logoLogin from "@/assets/images/logo-login.png";
import { Paths } from "@/core/constants/paths";
import { submitAccessRequest } from "@/modules/access";
import styles from "./request-access-screen.module.scss";

const JUSTIFICATION_MIN_LENGTH = 10;
const JUSTIFICATION_MAX_LENGTH = 200;
/** Validação básica de e-mail no front (local@domínio.tld). */
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const REQUIRED_FIELDS_MESSAGE = "Preencha os campos obrigatórios para continuar.";
const INVALID_EMAIL_MESSAGE = "Informe um e-mail válido para continuar.";
const FIELD_REQUIRED_MESSAGE = "Campo obrigatório";
const JUSTIFICATION_MIN_MESSAGE = `Mínimo de ${JUSTIFICATION_MIN_LENGTH} caracteres`;
const JUSTIFICATION_LENGTH_MESSAGE = `A justificativa deve ter entre ${JUSTIFICATION_MIN_LENGTH} e ${JUSTIFICATION_MAX_LENGTH} caracteres.`;
const SUCCESS_TITLE = "Solicitação Enviada!";
const SUCCESS_DESCRIPTION =
  "Sua solicitação de acesso foi enviada com sucesso. Você receberá um e-mail quando sua solicitação for aprovada.";

function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

type FieldErrors = {
  email?: string;
  justification?: string;
};

function getFormErrors(
  email: string,
  justification: string,
): {
  formMessage: string | null;
  fieldErrors: FieldErrors;
} {
  const fieldErrors: FieldErrors = {};

  if (!email) fieldErrors.email = FIELD_REQUIRED_MESSAGE;
  if (!justification) fieldErrors.justification = FIELD_REQUIRED_MESSAGE;

  if (fieldErrors.email || fieldErrors.justification) {
    return { formMessage: REQUIRED_FIELDS_MESSAGE, fieldErrors };
  }

  if (!isValidEmail(email)) {
    return {
      formMessage: INVALID_EMAIL_MESSAGE,
      fieldErrors: { email: "E-mail inválido" },
    };
  }

  if (justification.length < JUSTIFICATION_MIN_LENGTH) {
    return {
      formMessage: JUSTIFICATION_LENGTH_MESSAGE,
      fieldErrors: { justification: JUSTIFICATION_MIN_MESSAGE },
    };
  }

  return { formMessage: null, fieldErrors: {} };
}

export function RequestAccessScreen() {
  const [email, setEmail] = useState("");
  const [justification, setJustification] = useState("");
  const [formMessage, setFormMessage] = useState<string | null>(null);
  const [formStatus, setFormStatus] = useState<"error" | "success" | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSuccess = formStatus === "success";

  function handleJustificationChange(value: string) {
    setJustification(value.slice(0, JUSTIFICATION_MAX_LENGTH));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedEmail = email.trim();
    const trimmedJustification = justification.trim();
    const result = getFormErrors(trimmedEmail, trimmedJustification);

    setFormMessage(result.formMessage);
    setFieldErrors(result.fieldErrors);
    setFormStatus(result.formMessage ? "error" : null);

    if (result.formMessage) return;

    setIsSubmitting(true);

    try {
      await submitAccessRequest({
        email: trimmedEmail,
        justification: trimmedJustification,
      });

      setEmail("");
      setJustification("");
      setFieldErrors({});
      setFormMessage(null);
      setFormStatus("success");
    } catch (error) {
      setFormStatus("error");
      setFormMessage(
        error instanceof Error && error.message
          ? error.message
          : "Erro ao enviar solicitação. Tente novamente.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className={styles.page} data-testid="request-access-screen">
      <div className={styles.overlay} aria-hidden />

      <section
        className={styles.card}
        aria-labelledby={isSuccess ? "request-access-success-title" : "request-access-title"}
      >
        <div className={styles.brand}>
          <img
            className={styles.brandLogo}
            src={logoLogin}
            alt="Hub de Obras"
            width={88}
            height={34}
          />
        </div>

        {isSuccess ? (
          <>
            <div className={styles.header} data-testid="request-access-success">
              <h1 id="request-access-success-title" className={styles.title}>
                {SUCCESS_TITLE}
              </h1>
              <p className={styles.subtitle}>{SUCCESS_DESCRIPTION}</p>
            </div>

            <div className={styles.actions}>
              <p className={styles.loginHint}>
                Já tem acesso?{" "}
                <Link
                  className={styles.loginLink}
                  to={Paths.LOGIN}
                  data-testid="request-access-login-link"
                >
                  Fazer Login
                </Link>
              </p>
            </div>
          </>
        ) : (
          <>
            <div className={styles.header}>
              <h1 id="request-access-title" className={styles.title}>
                Solicitar Acesso
              </h1>
              <p className={styles.subtitle}>
                Solicite seu acesso e comece a gerenciar as obras com mais controle e eficiência.
              </p>
            </div>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <p className={styles.requiredLegend}>*Campos obrigatórios</p>

              {formMessage ? (
                <Tag
                  className={styles.errorTag}
                  severity="danger"
                  icon="pi pi-exclamation-triangle"
                  value={formMessage}
                  role="alert"
                  data-testid="request-access-error"
                />
              ) : null}

              <div className={styles.field}>
                <label className={styles.label} htmlFor="request-access-email">
                  E-mail <span aria-hidden>*</span>
                </label>
                <InputText
                  id="request-access-email"
                  className={[styles.input, fieldErrors.email ? styles.inputInvalid : ""]
                    .filter(Boolean)
                    .join(" ")}
                  type="email"
                  name="email"
                  placeholder="email@oceanpact.com"
                  value={email}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setEmail(event.currentTarget.value)
                  }
                  autoComplete="email"
                  disabled={isSubmitting}
                  aria-invalid={Boolean(fieldErrors.email)}
                  data-testid="request-access-email"
                />
                {fieldErrors.email ? (
                  <span className={styles.fieldError} data-testid="request-access-email-error">
                    {fieldErrors.email}
                  </span>
                ) : null}
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="request-access-justification">
                  Justificativa <span aria-hidden>*</span>
                </label>
                <InputTextarea
                  id="request-access-justification"
                  className={[styles.textarea, fieldErrors.justification ? styles.inputInvalid : ""]
                    .filter(Boolean)
                    .join(" ")}
                  name="justification"
                  value={justification}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                    handleJustificationChange(event.currentTarget.value)
                  }
                  maxLength={JUSTIFICATION_MAX_LENGTH}
                  disabled={isSubmitting}
                  aria-invalid={Boolean(fieldErrors.justification)}
                  data-testid="request-access-justification"
                />
                {fieldErrors.justification ? (
                  <span
                    className={styles.fieldError}
                    data-testid="request-access-justification-error"
                  >
                    {fieldErrors.justification}
                  </span>
                ) : null}
              </div>

              <div className={styles.actions}>
                <Button
                  type="submit"
                  className={styles.submitButton}
                  label="Solicitar Acesso"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  data-testid="request-access-submit"
                />

                <p className={styles.loginHint}>
                  Já tem acesso?{" "}
                  <Link
                    className={styles.loginLink}
                    to={Paths.LOGIN}
                    data-testid="request-access-login-link"
                  >
                    Fazer Login
                  </Link>
                </p>
              </div>
            </form>
          </>
        )}

        <footer className={styles.footer}>
          <p className={styles.copyright}>© 2026 OceanPact. Todos os direitos reservados.</p>
          <span className={styles.supportLink}>Precisa de ajuda? Contate o suporte</span>
        </footer>
      </section>
    </main>
  );
}
